import { Minus, Plus } from "lucide-react";
import { Button } from "../Button/index.js";

/* The minus/plus pair NumberInput renders at the end edge of its frame when
   `stepButtons` is on. Internal to the atom: not in the barrel.

   Both are DS Buttons (ghost, iconOnly, xs: the 24x24 step control). They
   are tabIndex -1, so the input stays the field's one tab stop; keyboard
   users step with ArrowUp/ArrowDown on the input (APG spinbutton). The
   wrapper cancels mousedown, so a press leaves focus in the input. It sits
   on the wrapper, not the buttons: a disabled Button has
   pointer-events-none, so a press on the button at a bound lands on the
   wrapper and still drops no focus. */

type Bound = number | string | undefined;

interface StepButtonsProps {
  readonly value: number | "";
  readonly step: number;
  readonly min: Bound;
  readonly max: Bound;
  /** The field's name, read as "Decrease <name>" and "Increase <name>". */
  readonly name: string | undefined;
  readonly disabled: boolean;
  readonly onStep: (value: number) => void;
}

/** A native min/max attribute as a number, or undefined when unset. */
function toBound(bound: Bound): number | undefined {
  if (bound === undefined || bound === "") return undefined;
  const n = Number(bound);
  return Number.isFinite(n) ? n : undefined;
}

/** Decimal places, so a 0.1 step from 0.2 lands on 0.3, not 0.30000000000000004. */
function decimals(n: number): number {
  return (String(n).split(".")[1] ?? "").length;
}

export function StepButtons({
  value,
  step,
  min,
  max,
  name,
  disabled,
  onStep,
}: StepButtonsProps) {
  const lo = toBound(min);
  const hi = toBound(max);
  // An empty field steps from 0; the result is clamped like any other.
  const base = value === "" ? 0 : value;
  const move = (direction: 1 | -1) => {
    const places = Math.max(decimals(base), decimals(step));
    let next = Number((base + direction * step).toFixed(places));
    if (lo !== undefined) next = Math.max(lo, next);
    if (hi !== undefined) next = Math.min(hi, next);
    onStep(next);
  };
  const atMin = value !== "" && lo !== undefined && value <= lo;
  const atMax = value !== "" && hi !== undefined && value >= hi;
  const named = (verb: string) => (name ? `${verb} ${name}` : verb);

  return (
    <span
      role="presentation"
      className="flex shrink-0 items-center gap-0.5"
      onMouseDown={(e) => e.preventDefault()}
    >
      <Button
        variant="ghost"
        iconOnly
        size="xs"
        tabIndex={-1}
        aria-label={named("Decrease")}
        disabled={disabled || atMin}
        onClick={() => move(-1)}
      >
        <Minus aria-hidden className="w-3.5 h-3.5" />
      </Button>
      <Button
        variant="ghost"
        iconOnly
        size="xs"
        tabIndex={-1}
        aria-label={named("Increase")}
        disabled={disabled || atMax}
        onClick={() => move(1)}
      >
        <Plus aria-hidden className="w-3.5 h-3.5" />
      </Button>
    </span>
  );
}
